/**
 * Satış Kanalları Ayarlar Sayfası (master-data CRUD).
 *
 * Düz master-data: kod (immutable) · ad · tip · aktif · sira · usage_count.
 * Komisyon oranlarındaki gibi tarihsel "dönem" mantığı YOKTUR — sade CRUD.
 *
 * Anayasa: M.1 (DB-driven) · M.2 (CRUD bütünlüğü: listele+ekle+düzenle+pasifleştir+sil) ·
 *          M.3 (kod immutable — edit'te disabled) · M.5 (çift katman yetki) ·
 *          M.6 (soft default + usage=0 ile hard delete).
 *
 * Backend:
 *  - GET    /api/satis-kanallari?include_inactive=true&with_usage=true
 *  - POST   /api/satis-kanallari
 *  - PATCH  /api/satis-kanallari/{id}        → ad/tip/aktif/sira (kod reddedilir)
 *  - DELETE /api/satis-kanallari/{id}        → soft (pasifleştir)
 *  - DELETE /api/satis-kanallari/{id}?hard=true → kalıcı (usage=0 ise)
 */

import React, { useEffect, useState } from 'react';
import Header from '../../components/Header';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { PERMISSIONS, hasPermission } from '../../auth/permissions';
import { styles, colors, radius } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Satış kanalı tipleri = sistem enum'u (yapısal sınıflama; müşteriye göre
// değişmeyen sabit liste — komisyon sayfasındaki KATEGORI_OPTIONS gibi).
const TIP_OPTIONS = [
  { value: 'FIZIKSEL', label: 'Mağaza / Fiziksel' },
  { value: 'ONLINE', label: 'Online / Web' },
  { value: 'PLATFORM', label: 'Platform (Yemeksepeti, Getir vb.)' },
  { value: 'DIGER', label: 'Diğer' },
];
const tipLabel = (v) => (TIP_OPTIONS.find((o) => o.value === v) || {}).label || v;

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: '13px',
  boxSizing: 'border-box',
};
const labelStyle = { fontSize: 12, color: '#666', display: 'block', marginBottom: 4 };

const EMPTY_CHANNEL = { kod: '', ad: '', tip: 'FIZIKSEL', sira: '', pos_onek: '' };

const POS_ONEK_HINT = 'POS ürün adı öneki — örn "PK." (Paket). Boş = öneksiz/varsayılan kanal (Mağaza).';

const SatisKanallariPage = () => {
  const toast = useToast();
  const { user } = useAuth();
  const canManage = hasPermission(user?.role, PERMISSIONS.MANAGE_SETTINGS, user?.effectivePermissions);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ ...EMPTY_CHANNEL });
  const [createError, setCreateError] = useState('');

  const [editModal, setEditModal] = useState(null); // {id, kod, ad, tip, aktif, sira}
  const [editError, setEditError] = useState('');

  // ─── Veri yükleme (sessiz refetch — refetch'te içerik sökülmez) ───
  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/satis-kanallari?include_inactive=true&with_usage=true`,
        { headers: authHeaders() },
      );
      const data = await res.json().catch(() => []);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      logError('Satış kanalları yüklenemedi:', err);
      toast.error('Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Yeni kanal ────────────────────────────────────────────────
  const openCreate = () => {
    setCreateForm({ ...EMPTY_CHANNEL });
    setCreateError('');
    setCreateModal(true);
  };

  const submitCreate = async () => {
    const { kod, ad, tip, sira, pos_onek } = createForm;
    if (!kod.trim()) return setCreateError('Kod zorunludur.');
    if (!ad.trim()) return setCreateError('Ad zorunludur.');

    setSaving(true);
    setCreateError('');
    try {
      const body = { kod: kod.trim(), ad: ad.trim(), tip, pos_onek: (pos_onek || '').trim() };
      if (String(sira).trim() !== '') body.sira = parseInt(sira, 10) || 0;
      const res = await fetch(`${API_URL}/api/satis-kanallari`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      toast.success('Kanal eklendi');
      setCreateModal(false);
      fetchAll();
    } catch (err) {
      setCreateError(err.message || 'Eklenemedi');
    } finally {
      setSaving(false);
    }
  };

  // ─── Düzenle (ad/tip/aktif/sira; kod sabit) ────────────────────
  const openEdit = (k) => {
    setEditModal({
      id: k.id,
      kod: k.kod,
      ad: k.ad || '',
      tip: k.tip || 'FIZIKSEL',
      aktif: k.aktif !== false,
      sira: k.sira != null ? String(k.sira) : '',
      pos_onek: k.pos_onek || '',
    });
    setEditError('');
  };

  const submitEdit = async () => {
    if (!editModal) return;
    if (!editModal.ad.trim()) return setEditError('Ad zorunludur.');
    setSaving(true);
    setEditError('');
    try {
      // NOT: kod bilinçli gönderilmez (M.3 immutable; backend de reddeder).
      const body = {
        ad: editModal.ad.trim(),
        tip: editModal.tip,
        aktif: editModal.aktif,
        pos_onek: (editModal.pos_onek || '').trim(),
      };
      if (String(editModal.sira).trim() !== '') body.sira = parseInt(editModal.sira, 10) || 0;
      const res = await fetch(`${API_URL}/api/satis-kanallari/${editModal.id}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      toast.success('Güncellendi');
      setEditModal(null);
      fetchAll();
    } catch (err) {
      setEditError(err.message || 'Güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  // ─── Pasifleştir (soft DELETE) / Aktifleştir (PATCH) ───────────
  const togglePasif = async (k) => {
    const isAktif = k.aktif !== false;
    try {
      let res;
      if (isAktif) {
        if (!window.confirm(`"${k.ad}" kanalı pasifleştirilsin mi? (geri alınabilir)`)) return;
        res = await fetch(`${API_URL}/api/satis-kanallari/${k.id}`, {
          method: 'DELETE',
          headers: authHeaders(),
        });
      } else {
        res = await fetch(`${API_URL}/api/satis-kanallari/${k.id}`, {
          method: 'PATCH',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ aktif: true }),
        });
      }
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      toast.success(isAktif ? 'Pasifleştirildi' : 'Aktifleştirildi');
      fetchAll();
    } catch (err) {
      toast.error(err.message || 'İşlem başarısız');
    }
  };

  // ─── Kalıcı sil (hard, usage=0) ────────────────────────────────
  const deleteHard = async (k) => {
    const usage = k.usage_count || 0;
    if (usage > 0) {
      toast.warning(`Silinemez: bu kanal ${usage} kayıtta kullanılıyor. "Pasifleştir" kullanın.`);
      return;
    }
    if (!window.confirm(`"${k.ad}" kanalı KALICI silinecek. Geri alınamaz. Devam?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/satis-kanallari/${k.id}?hard=true`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      toast.success('Kanal silindi');
      fetchAll();
    } catch (err) {
      toast.error(err.message || 'Silinemedi');
    }
  };

  // ─── Render ────────────────────────────────────────────────────
  if (loading && items.length === 0) {
    return (
      <div>
        <Header title="Satış Kanalları" />
        <div style={{ padding: 40, textAlign: 'center' }}>Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Satış Kanalları" />
      <div style={{ padding: 20, background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        <div
          style={{
            background: '#eef6ff',
            border: '1px solid #b6daff',
            color: '#0b5394',
            padding: '12px 16px',
            borderRadius: radius.md,
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          Satış kanalları (mağaza, web, platform vb.) burada yönetilir. <strong>Kod</strong> bir kez
          oluşturulduktan sonra değiştirilemez (referans bütünlüğü). Kullanımda olan bir kanal kalıcı
          silinemez — <strong>"Pasifleştir"</strong> ile gizleyebilirsiniz (geri alınabilir).
        </div>

        {canManage && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button
              onClick={openCreate}
              style={{
                padding: '10px 18px',
                background: '#c62828',
                color: 'white',
                border: 'none',
                borderRadius: radius.sm,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(198,40,40,0.25)',
              }}
            >
              + Yeni Kanal
            </button>
          </div>
        )}

        <div
          style={{
            background: colors.surface,
            borderRadius: radius.md,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            overflow: 'hidden',
          }}
        >
          {items.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: colors.textSecondary }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🛒</div>
              <div style={{ fontSize: 15, marginBottom: 4 }}>Henüz satış kanalı tanımlanmamış.</div>
              {canManage && (
                <div style={{ fontSize: 13 }}>
                  <strong>+ Yeni Kanal</strong> ile başlayın.
                </div>
              )}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 10 }}>Kod</th>
                  <th style={{ padding: 10 }}>Ad</th>
                  <th style={{ padding: 10 }}>Tip</th>
                  <th style={{ padding: 10 }}>POS Öneki</th>
                  <th style={{ padding: 10 }}>Durum</th>
                  <th style={{ padding: 10 }}>Kullanım</th>
                  {canManage && <th style={{ padding: 10, textAlign: 'right' }}>İşlem</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((k) => {
                  const isAktif = k.aktif !== false;
                  const usage = k.usage_count || 0;
                  return (
                    <tr
                      key={k.id}
                      style={{
                        borderBottom: `1px solid ${colors.border}`,
                        opacity: isAktif ? 1 : 0.55,
                        background: isAktif ? 'white' : '#fafafa',
                      }}
                    >
                      <td style={{ padding: 10, fontFamily: 'monospace', fontSize: 12 }}>{k.kod}</td>
                      <td style={{ padding: 10, fontWeight: 500 }}>{k.ad}</td>
                      <td style={{ padding: 10, color: '#555' }}>{tipLabel(k.tip)}</td>
                      <td style={{ padding: 10, fontFamily: 'monospace', fontSize: 12, color: '#555' }}>
                        {k.pos_onek || '—'}
                      </td>
                      <td style={{ padding: 10 }}>
                        <span
                          style={{
                            background: isAktif ? '#d4edda' : '#f8d7da',
                            color: isAktif ? '#155724' : '#721c24',
                            padding: '2px 8px',
                            borderRadius: radius.lg,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {isAktif ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td style={{ padding: 10 }}>
                        {usage > 0 ? (
                          <span style={{ background: '#fff3cd', color: '#856404', padding: '1px 6px', borderRadius: 8 }}>
                            {usage}
                          </span>
                        ) : (
                          <span style={{ color: '#aaa' }}>0</span>
                        )}
                      </td>
                      {canManage && (
                        <td style={{ padding: 10, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button
                            onClick={() => openEdit(k)}
                            title="Düzenle (kod sabittir)"
                            style={{
                              padding: '4px 10px',
                              background: '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                              marginRight: 4,
                            }}
                          >
                            ✏️ Düzenle
                          </button>
                          <button
                            onClick={() => togglePasif(k)}
                            style={{
                              padding: '4px 10px',
                              background: isAktif ? '#ffc107' : '#28a745',
                              color: isAktif ? '#333' : 'white',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                              marginRight: 4,
                            }}
                          >
                            {isAktif ? 'Pasifleştir' : 'Aktifleştir'}
                          </button>
                          <button
                            onClick={() => deleteHard(k)}
                            disabled={usage > 0}
                            title={usage > 0 ? 'Kullanımda, kalıcı silinemez' : 'Kalıcı sil'}
                            style={{
                              padding: '4px 10px',
                              background: usage > 0 ? '#ccc' : '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              cursor: usage > 0 ? 'not-allowed' : 'pointer',
                              fontSize: 12,
                            }}
                          >
                            🗑️ Sil
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ─── Yeni Kanal Modal ─────────────────────────────────────── */}
      {createModal && (
        <ModalShell
          onClose={() => !saving && setCreateModal(false)}
          title="Yeni Satış Kanalı"
          hint="Kod sonradan değiştirilemez (referans bütünlüğü). Tip P&L/raporlama sınıflamasıdır."
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Kod*</label>
              <input
                style={inputStyle}
                value={createForm.kod}
                autoFocus
                maxLength={50}
                onChange={(e) => setCreateForm({ ...createForm, kod: e.target.value.toUpperCase() })}
                placeholder="MAGAZA"
              />
            </div>
            <div>
              <label style={labelStyle}>Ad*</label>
              <input
                style={inputStyle}
                value={createForm.ad}
                maxLength={200}
                onChange={(e) => setCreateForm({ ...createForm, ad: e.target.value })}
                placeholder="Mağaza"
              />
            </div>
            <div>
              <label style={labelStyle}>Tip*</label>
              <select
                style={inputStyle}
                value={createForm.tip}
                onChange={(e) => setCreateForm({ ...createForm, tip: e.target.value })}
              >
                {TIP_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Sıra</label>
              <input
                style={inputStyle}
                type="number"
                value={createForm.sira}
                onChange={(e) => setCreateForm({ ...createForm, sira: e.target.value })}
                placeholder="0"
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>POS Öneki</label>
              <input
                style={inputStyle}
                value={createForm.pos_onek}
                maxLength={20}
                onChange={(e) => setCreateForm({ ...createForm, pos_onek: e.target.value })}
                placeholder='PK.'
              />
              <div style={{ ...labelStyle, marginTop: 4, marginBottom: 0 }}>{POS_ONEK_HINT}</div>
            </div>
          </div>
          <ModalError msg={createError} />
          <ModalActions saving={saving} onCancel={() => setCreateModal(false)} onSave={submitCreate} />
        </ModalShell>
      )}

      {/* ─── Düzenle Modal ────────────────────────────────────────── */}
      {editModal && (
        <ModalShell
          onClose={() => !saving && setEditModal(null)}
          title="Satış Kanalı Düzenle"
          hint="Kod sabittir (referans bütünlüğü). Ad, tip, sıra ve aktiflik düzenlenebilir."
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Kod (sabit)</label>
              <input
                style={{ ...inputStyle, background: '#f5f5f5', fontFamily: 'monospace', color: '#555' }}
                value={editModal.kod}
                disabled
                readOnly
              />
            </div>
            <div>
              <label style={labelStyle}>Ad*</label>
              <input
                style={inputStyle}
                value={editModal.ad}
                maxLength={200}
                onChange={(e) => setEditModal({ ...editModal, ad: e.target.value })}
              />
            </div>
            <div>
              <label style={labelStyle}>Tip*</label>
              <select
                style={inputStyle}
                value={editModal.tip}
                onChange={(e) => setEditModal({ ...editModal, tip: e.target.value })}
              >
                {TIP_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Sıra</label>
              <input
                style={inputStyle}
                type="number"
                value={editModal.sira}
                onChange={(e) => setEditModal({ ...editModal, sira: e.target.value })}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>POS Öneki</label>
              <input
                style={inputStyle}
                value={editModal.pos_onek}
                maxLength={20}
                onChange={(e) => setEditModal({ ...editModal, pos_onek: e.target.value })}
                placeholder='PK.'
              />
              <div style={{ ...labelStyle, marginTop: 4, marginBottom: 0 }}>{POS_ONEK_HINT}</div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ ...labelStyle, display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={editModal.aktif}
                  onChange={(e) => setEditModal({ ...editModal, aktif: e.target.checked })}
                />
                Aktif
              </label>
            </div>
          </div>
          <ModalError msg={editError} />
          <ModalActions saving={saving} onCancel={() => setEditModal(null)} onSave={submitEdit} />
        </ModalShell>
      )}
    </div>
  );
};

// ─── Ortak modal parçaları ───────────────────────────────────────
const ModalShell = ({ onClose, title, hint, children }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ background: 'white', borderRadius: radius.md, padding: 24, minWidth: 480, maxWidth: 580, boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}
    >
      <h3 style={{ marginTop: 0, marginBottom: 4 }}>{title}</h3>
      {hint && <p style={{ fontSize: 12, color: '#666', marginTop: 0, marginBottom: 16 }}>{hint}</p>}
      {children}
    </div>
  </div>
);

const ModalError = ({ msg }) =>
  msg ? (
    <div style={{ marginTop: 12, padding: '8px 12px', background: '#f8d7da', color: '#721c24', borderRadius: radius.sm, fontSize: 13 }}>
      {msg}
    </div>
  ) : null;

const ModalActions = ({ saving, onCancel, onSave }) => (
  <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
    <button
      onClick={onCancel}
      disabled={saving}
      style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: radius.sm, cursor: saving ? 'not-allowed' : 'pointer' }}
    >
      Vazgeç
    </button>
    <button
      onClick={onSave}
      disabled={saving}
      style={{ padding: '8px 16px', background: saving ? '#999' : styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}
    >
      {saving ? 'Kaydediliyor...' : 'Kaydet'}
    </button>
  </div>
);

export default SatisKanallariPage;
